import { useCallback, useEffect, useState } from 'react';
import { get, qs, ApiError, type Booking, type FollowUp, type SellerSummary, downloadFile } from '../lib/api.ts';
import { useSession, isAdmin } from '../auth/session.tsx';
import { cairoWorkDay, shortDate } from '../lib/format.ts';
import { Alert, Card, Spinner, TextInput } from '../components/ui.tsx';
import { QuickAddForm } from './QuickAddForm.tsx';
import { useOpenIntent, useOpenIntentWith } from '../lib/openIntent.ts';
import { CoachModal } from './Coaching.tsx';
import { scrollToAnchor } from '../lib/stickySection.ts';
import { LeadBankCard, type LeadPrefill } from './LeadBankCard.tsx';
import { WebLeadsCard } from './WebLeadsCard.tsx';
import { FollowUpList, type ListFilter } from './FollowUpList.tsx';
import { SOURCES } from '../lib/labels.ts';
import { FollowUpDetails } from './FollowUpDetails.tsx';
import { BookingFromFollowUp } from './BookingFromFollowUp.tsx';
import { FollowUpDoneModal } from './FollowUpDoneModal.tsx';
import { TeamDayPanel, type DayDrill } from './TeamDayPanel.tsx';

type View = 'today' | 'due' | 'overdue' | 'upcoming' | 'all' | 'search';
/** «كل المتابعات» صفحاتٌ من 50 (قرار المالك 01/10/2026: «يجيبلي أوّل 50 بس وبعد كده صفحة 2 فيها تاني 50 وهكذا») */
const PAGE = 50;
const VIEWS: { id: View; label: string }[] = [
  { id: 'today', label: 'متابعات اليوم' },
  { id: 'due', label: 'المستحقة اليوم' },
  { id: 'overdue', label: 'المتأخرة' },
  { id: 'upcoming', label: 'القادمة' },
  // كل السجلّ (قرار المالك 01/10/2026: «خلّي سجلّ المتابعات يظهر للبياع كلّه… History المتابعات كله يكون ظاهر»)
  { id: 'all', label: 'كل المتابعات' },
  { id: 'search', label: 'بحث وسجل العميل' },
];

export function CrmPage() {
  const { me } = useSession();
  const [view, setView] = useState<View>('today');
  const [page, setPage] = useState(0);
  /** فلاتر بالضغط (01/10/2026): بياعٌ وموقفٌ ونتيجةٌ وأولوية — تتجمّع وتُمسح كلٌّ بـ✕ */
  const [filters, setFilters] = useState<ListFilter[]>([]);
  const addFilter = (f: ListFilter) => {
    setFilters(cur => [...cur.filter(x => x.kind !== f.kind), f]);
    setPage(0);
    scrollToAnchor('follow-up-lists');
  };
  const dropFilter = (kind: ListFilter['kind']) => { setFilters(cur => cur.filter(x => x.kind !== kind)); setPage(0); };
  const filterKey = filters.map(f => `${f.kind}:${f.value}`).join('|');
  const [total, setTotal] = useState<number | null>(null);
  /** عدد كل مصدر في القائمة الحالية (01/10/2026: «والأفضل كمان يحط العدد») */
  const [sources, setSources] = useState<Record<string, number> | null>(null);
  const [exporting, setExporting] = useState(false);
  const [items, setItems] = useState<FollowUp[] | null>(null);
  const [summary, setSummary] = useState<SellerSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [details, setDetails] = useState<FollowUp | null>(null);
  /** «💡 توجيه» على متابعةٍ بعينها (0191) — للإدارة ولقارئ الفريق */
  const [coachFor, setCoachFor] = useState<FollowUp | null>(null);
  const [booking, setBooking] = useState<FollowUp | null>(null);
  /** «تحديث موقف المتابعة» (0151): المتابعة التي يتحدّث موقفها */
  const [doneFor, setDoneFor] = useState<FollowUp | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  /** تعبئة النموذج من بنك الأرقام — بمفتاحٍ يتغيّر مع كل ضغطة فيُعاد التطبيق ولو نفس الرقم */
  const [prefill, setPrefill] = useState<(LeadPrefill & { key: number }) | null>(null);
  const [bankTick, setBankTick] = useState(0);
  /**
   * **«👥 متابعات الفريق»** (0182، قرار المالك 03/10/2026: «عاوز محمد ابراهيم الشريك يكون هو كمان شايف المتابعات للشباب ال انا
   * بشوفها كلها بحيث يعرف يساعد او يقيم او يوجهه»): لمن سمح له المالك وليس أدمن — نفس القوائم والفلاتر بمتابعات الكل،
   * **قراءةً فقط** (لا تحديث موقف ولا حجز ولا تعديل على متابعة غيره؛ والخادم يفرضه).
   */
  // ثم (نفس اليوم): «عاوز هنا يكون ف متابعتاتى ومتابعات الفريق ولما اضغط على متابعات الفريق يجبلى كل واحد عمل كل يوم متابعات ايه
  // ونوعها ايه… وكذلك يبقى فيه كالندر» — فالمفتاح فوق الأرقام للمالك ولمحمد: «متابعاتي» = أرقامه هو، و«الفريق» = يوم كل بياع
  // (TeamDayPanel). والمالك يفتح على الفريق (أرقامه هو أصفارٌ لا تعنيه)، ومحمد على متابعاته.
  const admin = isAdmin(me);
  const canTeam = admin || me?.canViewTeamFollowUps === true;
  const [teamPick, setTeamPick] = useState<boolean | null>(null);
  const teamMode = canTeam && (teamPick ?? admin);
  /** قوائم الفريق لغير الأدمن (team=true) — وقوائم الأدمن كانت للكل أصلًا */
  const teamView = !admin && teamMode;
  const showSellers = admin || teamView;
  const pickTeam = (v: boolean) => { setTeamPick(v); setFilters([]); setPage(0); };
  /**
   * **رقمٌ في الكارت يُضغط ⇒ عملاؤه في القوائم تحت** (المالك 03/10/2026: «لما نضغط على حاجه فيهم سواء مهمه او تم الرد مثلا او
   * مهتم ينزلوا على قايمه العملاء دى ف اليوم وينقله الشاشه لتحت») — اليوم، والبياع (في الفريق)، والنوع، في «كل المتابعات».
   */
  const drill = (d: DayDrill) => {
    setFilters([
      ...(d.sellerId ? [{ kind: 'seller' as const, value: d.sellerId, label: d.sellerName ?? '' }] : []),
      { kind: 'day' as const, value: d.day, label: `يوم ${shortDate(d.day)}` },
      ...(d.filter ? [d.filter] : []),
    ]);
    setView('all'); setPage(0);
    scrollToAnchor('follow-up-lists');
  };

  /**
   * «تسجيل متابعة جديدة» من الصفحة الرئيسية: النموذج موجودٌ في هذه الشاشة دائمًا، فلا
   * شاشةَ تُفتح له — بل يُنزل به إلى العين ويوضع المؤشّر في أوّل حقوله.
   */
  useOpenIntent('crm', 'new', () => {
    const box = document.getElementById('quick-add-form');
    box?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    box?.querySelector<HTMLInputElement>('input, select, textarea')?.focus();
  });

  /**
   * **كارتٌ واحد لكل عميل بآخر موقفه** (قرار المالك 01/10/2026: «بدل سجل جديد، خلّيها تحديث موقف المتابعة»): القوائم
   * كلّها latest — والمواقف السابقة في «سجل العميل» داخل التفاصيل.
   */
  /**
   * **من «مستحقة النهاردة» إلى العملاء مباشرةً** (قرار المالك 01/10/2026: «لما أضغط على مستحقة اليوم البرنامج يروح
   * للعملاء اللي محتاجين متابعة اليوم بحيث يتعامل معاهم على طول»): خانتا الصفحة الرئيسية تفتحان هذه الشاشة على
   * قائمة «المستحقة اليوم» أو «المتأخرة» وتنزلان إليها — وعلى كل عميلٍ «🔄 تحديث الموقف».
   */
  useOpenIntent('crm', 'due', () => { setView('due'); scrollToAnchor('follow-up-lists'); });
  // من إشعار التوجيه (0191): «افتح العميل» يفتح المتابعة نفسها
  useOpenIntentWith('crm', 'follow-up', id => { void get<FollowUp>(`/follow-ups/${id}`).then(setDetails).catch(() => setError('المتابعة مش موجودة أو مش من صلاحياتك')); });
  useOpenIntent('crm', 'overdue', () => { setView('overdue'); scrollToAnchor('follow-up-lists'); });

  const query = useCallback((v: View, term: string): string => {
    const t = term.trim();
    // من كارت اليوم (03/10/2026): «day» هو on للخادم، وبه كل صفوف اليوم لا آخر موقفٍ لكل عميل — فالعدد يطابق الكارت
    const dayPick = filters.some(f => f.kind === 'day');
    const latest = dayPick ? undefined : 'true';
    const by = { ...Object.fromEntries(filters.map(f => [f.kind === 'day' ? 'on' : f.kind, f.value])), ...(teamView ? { team: 'true' } : {}) };
    if (v === 'today') return qs({ on: cairoWorkDay(), latest, limit: 200, ...by });   // يوم الشغل (4 الفجر)
    if (v === 'due') return qs({ due: 'today', sort: 'next', latest, limit: 200, ...by });
    if (v === 'overdue') return qs({ due: 'overdue', sort: 'next', latest, limit: 200, ...by });
    if (v === 'upcoming') return qs({ due: 'upcoming', sort: 'next', latest, limit: 200, ...by });
    if (v === 'all') return qs({ sort: 'newest', latest, limit: PAGE, offset: page * PAGE, withTotal: 'true', ...by });
    return /\d/.test(t) ? qs({ phone: t, latest, limit: 200, ...by }) : qs({ q: t, latest, limit: 200, ...by });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filterKey, teamView]);

  const load = useCallback(async (v: View = view, term: string = search) => {
    if (v === 'search' && !term.trim()) { setItems([]); return; }
    setError(null);
    try {
      const r = await get<{ items: FollowUp[]; total?: number; sources?: Record<string, number> }>(`/follow-ups${query(v, term)}&withSources=true`);
      setSources(r.sources ?? null);
      setItems(r.items);
      setTotal(typeof r.total === 'number' ? r.total : null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'تعذّر تحميل المتابعات');
      setItems([]);
    }
  }, [query, search, view]);



  const loadSummary = useCallback(async () => {
    try {
      const s = await get<SellerSummary>('/me/crm/summary');
      // لا نعرض لوحة ناقصة: أي رد غير متوقع يُتجاهل بدل أن يكسر الصفحة
      setSummary(s && s.today && s.dueFollowUps && s.totals ? s : null);
    } catch { setSummary(null); }
  }, []);

  useEffect(() => { void load(view, search); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [view, page, filterKey, teamView]);
  useEffect(() => { void loadSummary(); }, [loadSummary]);

  const refresh = useCallback(() => { void load(); void loadSummary(); }, [load, loadSummary]);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold">المتابعات اليومية</h1>
        <p className="text-sm text-ink-500">سجّل تواصلك مع العملاء اليوم وتابع المواعيد القادمة — {me?.fullNameAr}</p>
      </div>

      {flash && <Alert kind="success">{flash}</Alert>}
      {error && <Alert>{error}</Alert>}

      {canTeam && (
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="متابعات مين">
          {[{ v: false, label: '📋 متابعاتي' }, { v: true, label: '👥 متابعات الفريق' }].map(o => (
            <button key={String(o.v)} type="button" aria-pressed={teamMode === o.v} onClick={() => pickTeam(o.v)}
              className={`rounded-xl border px-4 py-2 text-sm font-semibold ${teamMode === o.v ? 'border-brand-600 bg-brand-600 text-white shadow-sm' : 'border-slate-200 bg-white text-ink-700 hover:border-brand-300'}`}>
              {o.label}
            </button>
          ))}
          {teamView && <span className="text-xs text-ink-500">متابعات كل الشباب — للقراءة والتوجيه بس، والتعديل لصاحب المتابعة</span>}
        </div>
      )}

      {me && teamMode && (
        <TeamDayPanel onDrill={drill} />
      )}

      {/* «متابعاتي» بنفس كارت الفريق وتقويمه (المالك 03/10/2026: «خلى كل واحد من الشباب برضو يظهرله متابعاته كدا بنفس التقسيم
          دا مش التقسيم الاول») — والمستحق والمتأخر سطرٌ تحته، يُضغط فيفتح قائمته */}
      {me && !teamMode && (
        <>
          <TeamDayPanel mine onDrill={drill} />
          {summary && (
            <div className="flex flex-wrap gap-2 text-sm">
              <button type="button" className="rounded-full border border-slate-200 bg-white px-3 py-1 font-semibold text-ink-700 hover:border-brand-300"
                onClick={() => { setView('due'); scrollToAnchor('follow-up-lists'); }}>
                مستحقة اليوم <b className="tabular-nums">{summary.dueFollowUps.dueToday}</b>
              </button>
              <button type="button" className={`rounded-full border px-3 py-1 font-semibold hover:border-brand-300 ${summary.dueFollowUps.overdue > 0 ? 'border-rose-200 bg-rose-50 text-rose-700' : 'border-slate-200 bg-white text-ink-700'}`}
                onClick={() => { setView('overdue'); scrollToAnchor('follow-up-lists'); }}>
                متأخرة <b className="tabular-nums">{summary.dueFollowUps.overdue}</b>
              </button>
              <span className="rounded-full border border-slate-200 bg-white px-3 py-1 font-semibold text-ink-700">
                حجوزات من المتابعات <b className="tabular-nums">{summary.totals.bookingsFromFollowUps}</b>
              </span>
            </div>
          )}
        </>
      )}

      {/* طلبات الموقع (0113): ما جاء عليك الدور فيه — فوق بنك الأرقام لأن العميل ينتظر الآن */}
      {/* الأدمن يرى ما أخذه لنفسه من طلبات الموقع ليردّ عليه (قرار المالك 23/09/2026) */}
      <div id="web-leads" className="scroll-mt-24" />
      <WebLeadsCard key={`web-${bankTick}`} mine={isAdmin(me)} meId={me?.id ?? null} canDelete={!!me?.canDeleteWebLeads} onUse={p => {
          setPrefill({ ...p, key: Date.now() });
          document.getElementById('quick-add-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }} />

      {/* بنك الأرقام (0104): للبياع والشريك — الأدمن يرى الأعداد في «نشاط الشركة» */}
      <div id="lead-bank" className="scroll-mt-24" />
      {!isAdmin(me) && (
        <LeadBankCard key={bankTick} onUse={p => {
          setPrefill({ ...p, key: Date.now() });
          document.getElementById('quick-add-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }} />
      )}

      {/* «تسجيل متابعة جديدة» من الصفحة الرئيسية: النموذج موجودٌ هنا دائمًا، فالنيّة
          تنزل به إلى العين وتضع المؤشّر في أوّل حقوله — لا شاشةٌ ثانية تُفتح له. */}
      <div id="quick-add-form">
        <Card title="تسجيل متابعة">
          <QuickAddForm prefill={prefill} onAdded={() => { refresh(); setBankTick(t => t + 1); }} />
        </Card>
      </div>

      <div id="follow-up-lists" className="scroll-mt-24" />
      <Card
        title="قوائم المتابعات"
        action={
          <div className="flex flex-wrap gap-2">
            {VIEWS.map(v => (
              <button key={v.id} type="button" onClick={() => { setView(v.id); setPage(0); }} aria-current={view === v.id ? 'true' : undefined}
                className={`rounded-xl border px-4 py-2.5 text-sm font-semibold transition-all duration-150 ${view === v.id ? 'border-brand-600 bg-brand-600 text-white shadow-sm shadow-brand-600/30' : 'border-slate-200 bg-white text-ink-700 shadow-sm hover:-translate-y-px hover:border-brand-300 hover:text-brand-700'}`}>
                {v.label}
              </button>
            ))}
          </div>
        }
      >
        {view === 'all' && (
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-ink-600">{teamView ? 'كل متابعات الفريق' : 'كل متابعاتك'} من الأحدث للأقدم{total !== null ? ` — ${total} عميل · صفحة ${page + 1} من ${Math.max(1, Math.ceil(total / PAGE))}` : ''}.</p>
            {/* شيت Excel (قرار المالك 01/10/2026): متابعاتك أنت وحدك، والأدمن الكل */}
            {!teamView && <button type="button" className="btn-ghost px-3 py-1.5 text-sm" disabled={exporting}
              onClick={() => { setExporting(true); void downloadFile('/exports/follow-ups.xlsx', 'المتابعات.xlsx').catch(e => setError(e instanceof ApiError ? e.message : 'تعذّر تنزيل الشيت')).finally(() => setExporting(false)); }}>
              {exporting ? 'جارٍ التجهيز…' : '⬇️ شيت Excel'}
            </button>}
          </div>
        )}
        {view === 'search' && (
          <form className="mb-3 flex gap-2" onSubmit={e => { e.preventDefault(); void load('search', search); }} aria-label="بحث عن عميل">
            <TextInput value={search} onChange={e => setSearch(e.target.value)} placeholder="اسم العميل أو رقم الهاتف" aria-label="اسم العميل أو رقم الهاتف" />
            <button type="submit" className="btn-primary">بحث</button>
          </form>
        )}
        {/* **أزرار المصدر بالعدد** (قرار المالك 01/10/2026: «نضغط على بنك الأرقام يظهر البنك فقط، ولو جديد يظهر الجديد…
            والأفضل كمان يحط العدد») — العدد بنفس الفلاتر الأخرى، والضغطة الثانية ترجع للكل */}
        {sources && (
          <div className="mb-3 flex flex-wrap items-center gap-1.5" role="group" aria-label="المصدر">
            <span className="text-xs font-semibold text-ink-500">المصدر:</span>
            {(() => {
              const cur = filters.find(f => f.kind === 'source')?.value ?? null;
              const all = Object.values(sources).reduce((a, n) => a + n, 0);
              const chip = (active: boolean) => `rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${active
                ? 'border-brand-600 bg-brand-600 text-white' : 'border-slate-200 bg-white text-ink-700 hover:border-brand-300'}`;
              return (
                <>
                  <button type="button" className={chip(cur === null)} aria-pressed={cur === null} onClick={() => dropFilter('source')}>الكل ({all})</button>
                  {SOURCES.map(src => (
                    <button key={src.code} type="button" className={chip(cur === src.code)} aria-pressed={cur === src.code}
                      onClick={() => (cur === src.code ? dropFilter('source') : addFilter({ kind: 'source', value: src.code, label: src.ar }))}>
                      {src.icon} {src.ar} ({sources[src.code] ?? 0})
                    </button>
                  ))}
                </>
              );
            })()}
          </div>
        )}
        {filters.length > 0 && (
          <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg bg-brand-50 px-3 py-2 text-sm" role="status" aria-label="الفلاتر الحالية">
            <span className="font-semibold text-brand-800">معروض بس:</span>
            {filters.map(f => (
              <span key={f.kind} className="inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 font-semibold text-ink-800 shadow-sm">
                {f.kind === 'seller' ? '👤 ' : f.kind === 'day' ? '📅 ' : ''}{f.label}
                <button type="button" aria-label={`شيل فلتر ${f.label}`} onClick={() => dropFilter(f.kind)}
                  className="grid h-5 w-5 place-items-center rounded-full text-xs text-ink-500 hover:bg-slate-100">✕</button>
              </span>
            ))}
            <button type="button" className="ms-auto text-xs font-semibold text-brand-700 hover:underline" onClick={() => { setFilters([]); setPage(0); }}>اعرض الكل</button>
          </div>
        )}
        {items === null ? <Spinner /> : (
          <FollowUpList
            onFilter={addFilter}
            items={items}
            showSeller={showSellers}
            onOpen={setDetails}
            onBook={teamView ? undefined : setBooking}
            onDone={teamView ? undefined : setDoneFor}
            onCoach={showSellers ? setCoachFor : undefined}
            onCoachingChanged={refresh}
            empty={view === 'search' ? 'اكتب اسمًا أو رقمًا للبحث في متابعاتك' : 'لا توجد متابعات في هذه القائمة'}
          />
        )}
        {view === 'all' && total !== null && total > PAGE && (
          <Pager page={page} pages={Math.ceil(total / PAGE)} onGo={n => { setPage(n); scrollToAnchor('follow-up-lists'); }} />
        )}
      </Card>

      {coachFor && <CoachModal f={coachFor} onClose={() => setCoachFor(null)} onSent={refresh} />}
      {details && (
        <FollowUpDetails
          followUp={details} isAdminUser={isAdmin(me)}
          readOnly={teamView && details.salesUserId !== me?.id}
          onClose={() => setDetails(null)}
          onSaved={() => refresh()}
          onDeleted={() => { setDetails(null); refresh(); }}
          onBook={f => { setDetails(null); setBooking(f); }}
          onDone={f => { setDetails(null); setDoneFor(f); }}
        />
      )}

      {doneFor && (
        <FollowUpDoneModal
          followUp={doneFor} showSeller={isAdmin(me)}
          onClose={() => setDoneFor(null)}
          onDone={created => {
            setDoneFor(null);
            setFlash(`✓ اتحدّث موقف متابعة ${created.customerName}${created.nextFollowUpOn ? ' — والمتابعة الجاية في يومها' : ''}`);
            refresh(); setBankTick(t => t + 1);
          }}
        />
      )}

      {booking && (
        <BookingFromFollowUp
          followUp={booking}
          isAdminUser={isAdmin(me)}
          onClose={() => setBooking(null)}
          onCreated={(_f: FollowUp, b: Booking) => {
            setBooking(null);
            setFlash(`تم إنشاء الحجز ${b.bookingNo} وربطه بالمتابعة`);
            refresh();
          }}
        />
      )}
    </div>
  );
}

/**
 * **صفحات «كل المتابعات»** (قرار المالك 01/10/2026): السابق والتالي، وأرقام الصفحات القريبة — الأولى والأخيرة دائمًا وما
 * حول الحالية، وبينها «…».
 */
function Pager({ page, pages, onGo }: { page: number; pages: number; onGo: (n: number) => void }) {
  const near = new Set([0, pages - 1, page - 1, page, page + 1].filter(n => n >= 0 && n < pages));
  const list = [...near].sort((a, b) => a - b);
  const btn = 'min-w-[2.25rem] rounded-lg border px-2.5 py-1.5 text-sm font-semibold transition-colors';
  return (
    <nav aria-label="صفحات المتابعات" className="mt-4 flex flex-wrap items-center justify-center gap-1.5">
      <button type="button" className={`${btn} border-slate-200 bg-white text-ink-700 hover:border-brand-300 disabled:opacity-40`}
        disabled={page === 0} onClick={() => onGo(page - 1)}>› السابق</button>
      {list.map((n, i) => (
        <span key={n} className="flex items-center gap-1.5">
          {i > 0 && n - list[i - 1] > 1 && <span className="text-ink-400">…</span>}
          <button type="button" aria-current={n === page ? 'page' : undefined} aria-label={`صفحة ${n + 1}`}
            className={`${btn} ${n === page ? 'border-brand-600 bg-brand-600 text-white' : 'border-slate-200 bg-white text-ink-700 hover:border-brand-300'}`}
            onClick={() => onGo(n)}>{n + 1}</button>
        </span>
      ))}
      <button type="button" className={`${btn} border-slate-200 bg-white text-ink-700 hover:border-brand-300 disabled:opacity-40`}
        disabled={page >= pages - 1} onClick={() => onGo(page + 1)}>التالي ‹</button>
    </nav>
  );
}
